import axios from 'axios'
import { key_, base } from '@/config/api'

// Axios instance for OpenWeather API
const openWeatherClient = axios.create({
  baseURL: base,
  headers: {
    'Content-Type': 'application/json'
  }
});

/**
 * Fetch current weather for a location
 * @param {number} lat - Latitude
 * @param {number} lon - Longitude
 * @param {string} units - Units (metric, imperial)
 * @returns {Promise<Object>} - Current weather data
 */
export async function fetchCurrentWeather(lat, lon, units = 'metric') {
  const response = await openWeatherClient.get('/weather', {
    params: {
      lat,
      lon,
      units,
      appid: key_
    }
  });

  return response.data;
}

/**
 * Fetch weather forecast for a location
 * @param {number} lat - Latitude
 * @param {number} lon - Longitude
 * @param {string} units - Units (metric, imperial)
 * @returns {Promise<Object>} - Forecast data
 */
export async function fetchWeatherForecast(lat, lon, units = 'metric') {
  const response = await openWeatherClient.get('/forecast', {
    params: {
      lat,
      lon,
      units,
      appid: key_
    }
  });

  return response.data;
}

/**
 * Fetch air quality data for a location
 * @param {number} lat - Latitude
 * @param {number} lon - Longitude
 * @returns {Promise<Object>} - Air quality data
 */
export async function fetchAirQuality(lat, lon) {
  const response = await openWeatherClient.get('/air_pollution', {
    params: {
      lat,
      lon,
      appid: key_
    }
  });

  return response.data;
}

/**
 * Fetch weather and air quality data for a location
 * @param {number} lat - Latitude
 * @param {number} lon - Longitude
 * @param {string} units - Units (metric, imperial)
 * @returns {Promise<Object>} - Combined weather and air quality data
 */
export async function fetchWeatherAndAirQuality(lat, lon, units = 'metric') {
  const [weatherData, airQualityData] = await Promise.all([
    fetchCurrentWeather(lat, lon, units),
    fetchAirQuality(lat, lon)
  ]);

  return {
    weather: weatherData,
    airQuality: airQualityData
  };
}

/**
 * Fetch comprehensive weather data for a location
 * @param {Object} params - Parameters for the weather request
 * @param {number} params.lat - Latitude
 * @param {number} params.lon - Longitude
 * @param {string} params.location - Location name (alternative to coordinates)
 * @param {string} params.units - Units (metric, imperial)
 * @returns {Promise<Object>} - Weather data
 * @throws {Error} - If the request fails or parameters are invalid
 */
export async function fetchWeatherData(params) {
  // Validate parameters
  if (!params.location && (!params.lat || !params.lon)) {
    throw new Error('Either location name or coordinates (lat/lon) are required');
  }

  if (params.units && !['metric', 'imperial'].includes(params.units)) {
    throw new Error('Invalid units parameter. Must be either "metric" or "imperial"');
  }

  // If coordinates are provided, validate them
  if (params.lat && params.lon) {
    if (typeof params.lat !== 'number' || typeof params.lon !== 'number' ||
        isNaN(params.lat) || isNaN(params.lon) ||
        params.lat < -90 || params.lat > 90 ||
        params.lon < -180 || params.lon > 180) {
      throw new Error('Invalid coordinates provided');
    }
  }

  try {
    // If location is provided, geocode it to get coordinates
    if (params.location && (!params.lat || !params.lon)) {
      try {
        const geocodeResponse = await axios.get('https://api.openweathermap.org/geo/1.0/direct', {
          params: {
            q: params.location,
            limit: 1,
            appid: key_
          }
        });

        if (!geocodeResponse.data || geocodeResponse.data.length === 0) {
          throw new Error(`Location not found: ${params.location}`);
        }

        params.lat = geocodeResponse.data[0].lat;
        params.lon = geocodeResponse.data[0].lon;
      } catch (error) {
        if (error.response?.status === 404) {
          throw new Error(`Location not found: ${params.location}`);
        }
        throw new Error(`Failed to geocode location: ${error.message}`);
      }
    }

    // Fetch all weather data in parallel with individual error handling
    const [current, forecast, airQuality] = await Promise.allSettled([
      fetchCurrentWeather(params.lat, params.lon, params.units),
      fetchWeatherForecast(params.lat, params.lon, params.units),
      fetchAirQuality(params.lat, params.lon)
    ]);

    // Handle any rejected promises
    const errors = [];
    if (current.status === 'rejected') {
      errors.push(`Current weather: ${current.reason.message}`);
    }
    if (forecast.status === 'rejected') {
      errors.push(`Forecast: ${forecast.reason.message}`);
    }
    if (airQuality.status === 'rejected') {
      errors.push(`Air quality: ${airQuality.reason.message}`);
    }

    if (errors.length > 0) {
      throw new Error(`Failed to fetch some weather data: ${errors.join('; ')}`);
    }

    // Get location name if not provided
    let locationName = params.location;
    if (!locationName && current.value && current.value.name) {
      locationName = current.value.name;
      if (current.value.sys && current.value.sys.country) {
        locationName += `, ${current.value.sys.country}`;
      }
    }

    // Return combined data
    return {
      location: locationName,
      coordinates: {
        latitude: params.lat,
        longitude: params.lon
      },
      current: current.value,
      forecast: forecast.value,
      airQuality: airQuality.value,
      timestamp: new Date().toISOString(),
      units: params.units || 'metric'
    };
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('An unexpected error occurred while fetching weather data');
  }
}

/**
 * Get weather map tile URL for a specific layer type
 * @param {string} layerType - Type of weather layer ('precipitation', 'temperature', 'wind', 'pressure', 'clouds')
 * @returns {string} - Tile URL template for the specified layer
 */
export function getWeatherMapTileUrl(layerType = 'precipitation') {
  if (!key_) {return '';
  }

  // Map the layer type to the OpenWeatherMap layer name
  const layerMap = {
    precipitation: 'precipitation_new',
    temperature: 'temp_new',
    wind: 'wind_new',
    pressure: 'pressure_new',
    clouds: 'clouds_new'
  };

  const layer = layerMap[layerType] || 'precipitation_new';
  return `https://tile.openweathermap.org/map/${layer}/{z}/{x}/{y}.png?appid=${key_}`;
}

/**
 * Get user's current location
 * @returns {Promise<{lat: number, lon: number}>} - Coordinates
 */
export function getCurrentLocation() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by your browser'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          lat: position.coords.latitude,
          lon: position.coords.longitude
        });
      },
      (error) => {
        reject(error);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  });
}
